import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

const GUTTER = 12; // min distance from the viewport edge
const GAP = 8; // distance between icon and bubble (arrow included via CSS)
const ARROW = 6;

/**
 * Dependency-free tooltip.
 *
 * Visibility on mouse hover is handled purely by CSS (`:hover` on the trigger,
 * see `.app-tooltip-trigger` in custom.scss). The browser keeps that state in
 * sync with the cursor itself - including after the page re-flows under a
 * stationary cursor - so the bubble shows exactly when the help cursor does.
 *
 * JavaScript only:
 *  - positions the bubble (fixed, clamped to the viewport, flips below when
 *    there is no room above),
 *  - toggles it on touch tap / Enter / Space, closes it on Escape or an
 *    outside tap (class `is-open`),
 *  - calls `onActivate` when the trigger is clicked.
 *
 * Props: `content`, `className` (`small-size` => compact), `onActivate`.
 */
const Tooltip = ({ children, content, className = "", onActivate }) => {
  const id = useId();
  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  const pointerTypeRef = useRef("mouse");
  const frameRef = useRef(null);
  const [pinned, setPinned] = useState(false);
  const [layout, setLayout] = useState(null); // { top, left, arrowLeft, side }

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;
    const r = trigger.getBoundingClientRect();
    if (!r.width && !r.height) return;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const w = bubble.offsetWidth;
    const h = bubble.offsetHeight;
    const distance = GAP + ARROW;

    let side = "top";
    let top = r.top - h - distance;
    if (top < GUTTER && r.bottom + distance + h <= vh - GUTTER) {
      side = "bottom";
      top = r.bottom + distance;
    }
    top = Math.max(GUTTER, Math.min(top, vh - h - GUTTER));

    const centre = r.left + r.width / 2;
    const left = Math.max(GUTTER, Math.min(centre - w / 2, vw - w - GUTTER));
    const arrowLeft = Math.max(ARROW + 2, Math.min(centre - left, w - ARROW - 2));

    setLayout((prev) =>
      prev && prev.top === top && prev.left === left && prev.arrowLeft === arrowLeft && prev.side === side
        ? prev
        : { top, left, arrowLeft, side }
    );
  }, []);

  const schedule = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      measure();
    });
  }, [measure]);

  // Position before paint whenever content changes (e.g. "Copied!").
  useLayoutEffect(() => {
    measure();
  }, [content, measure]);

  // Keep the position right while the page moves under the pointer.
  useEffect(() => {
    const onChange = () => schedule();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onChange) : null;
    ro?.observe(document.body);
    if (document.fonts) document.fonts.addEventListener("loadingdone", onChange);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
      ro?.disconnect();
      if (document.fonts) document.fonts.removeEventListener("loadingdone", onChange);
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
    };
  }, [schedule]);

  // Outside tap / Escape close a pinned (touch or keyboard) tooltip.
  useEffect(() => {
    if (!pinned) return undefined;
    const onPointerDown = (event) => {
      if (!triggerRef.current?.contains(event.target)) setPinned(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setPinned(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [pinned]);

  const isCompact = className.includes("small-size");
  const bubbleStyle = layout
    ? { position: "fixed", top: layout.top, left: layout.left, "--app-tooltip-arrow-left": `${layout.arrowLeft}px` }
    : undefined;

  return (
    <span
      ref={triggerRef}
      className={`app-tooltip-trigger ${pinned ? "is-open" : ""}`}
      tabIndex={0}
      aria-label={typeof content === "string" ? content : "More information"}
      aria-describedby={id}
      onPointerDown={(event) => {
        pointerTypeRef.current = event.pointerType;
      }}
      onPointerEnter={(event) => {
        pointerTypeRef.current = event.pointerType;
        measure();
      }}
      onMouseOver={schedule}
      onPointerMove={schedule}
      onFocus={measure}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onActivate?.();
        const hoverCapable = pointerTypeRef.current === "mouse" || pointerTypeRef.current === "pen";
        if (!hoverCapable) setPinned((open) => !open); // touch: tap toggles
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          setPinned((open) => !open);
        } else if (event.key === "Escape") {
          setPinned(false);
          event.currentTarget.blur();
        }
      }}
      onBlur={() => setPinned(false)}
    >
      {children}
      <span
        ref={bubbleRef}
        id={id}
        role="tooltip"
        className={`app-tooltip app-tooltip--${layout ? layout.side : "top"} ${isCompact ? "app-tooltip--compact" : ""}`}
        style={bubbleStyle}
      >
        {content}
        <span className="app-tooltip__arrow" aria-hidden="true" />
      </span>
    </span>
  );
};

export default Tooltip;
